import React, { useState, useEffect, useRef } from "react";
import {
  Building2,
  UserPlus,
  RefreshCw,
  Mail,
  Phone,
  Eye,
  Edit,
  MapPin,
  Search,
} from "lucide-react";
import toast from "react-hot-toast";
import { Input } from "@/components/ui/input";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const CompaniesTab = ({ onAddCompany, onEditCompany, refreshTrigger }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debounceTimer = useRef(null);
  const isInitialMount = useRef(true);

  const fetchCompanies = async (search = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        page: "1",
        limit: "100",
      });
      const url = `${BACKEND_URL}/api/company?${params}`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        // API returns an object with companies array containing full company objects
        const companiesList = data.companies || [];
        // Sort by created_at in descending order (newest first)
        const sortedCompanies = companiesList.sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
          const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
          return dateB - dateA;
        });
        setCompanies(sortedCompanies);
      } else {
        toast.error("Failed to load companies");
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount and when refreshTrigger changes
  useEffect(() => {
    fetchCompanies();
    // Reset search query when refresh trigger changes
    setSearchQuery("");
    isInitialMount.current = true;
  }, [refreshTrigger]);

  // Debounced search effect
  useEffect(() => {
    // Skip on initial mount when searchQuery is empty
    if (isInitialMount.current && searchQuery === "") {
      isInitialMount.current = false;
      return;
    }
    isInitialMount.current = false;

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      fetchCompanies(searchQuery);
    }, 300); // 300ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery]);

  const getStatusColor = (status) => {
    const colors = {
      "Active Account": "bg-green-100 text-green-800",
      "Qualified Lead": "bg-blue-100 text-blue-800",
      DNC: "bg-red-100 text-red-800",
      Archive: "bg-gray-100 text-gray-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const formatPhone = (countryCode, number) => {
    if (!number) return "-";
    const phoneNumber = typeof number === "number" ? number.toString() : number;
    // Format phone number with dashes if it's a long number
    const formatted =
      phoneNumber.length === 10
        ? `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
            3,
            6
          )}-${phoneNumber.slice(6)}`
        : phoneNumber;
    return `${countryCode || "+1"} ${formatted}`;
  };

  const formatLocation = (city, state, country) => {
    const parts = [city, state, country].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-purple-600" />
            Companies
          </h2>
          <p className="text-gray-600 mt-1">Manage and view all companies</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchCompanies(searchQuery)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={onAddCompany}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <UserPlus className="h-4 w-4" />
            Add Company
          </button>
        </div>
      </div>

      {/* Search Box */}
      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div> */}

      {/* Companies Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      ) : companies.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No companies found
          </h3>
          <p className="text-gray-600 mb-4">
            Get started by adding your first company
          </p>
          <button
            onClick={onAddCompany}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Add Company
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Company Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[140px]">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Industry
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Account Manager
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {companies.map((company, index) => (
                <tr key={company.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                        {(company.company_name &&
                          String(company.company_name)
                            .charAt(0)
                            ?.toUpperCase()) ||
                          "C"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 break-words">
                          {company.company_name || "-"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 min-w-[140px]">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(
                        company.status
                      )}`}
                    >
                      {company.status || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {company.industry || "-"}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {formatLocation(
                          company.city,
                          company.state,
                          company.country
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-600 whitespace-nowrap">
                        {formatPhone(
                          company.main_phone_country_code,
                          company.main_phone_number
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {company.account_manager || "-"}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        className="text-purple-600 hover:text-purple-700"
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onEditCompany(company)}
                        className="text-blue-600 hover:text-blue-700"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CompaniesTab;
